/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Button, H6, Icon, InputGroup, Menu, MenuItem, Popover, NonIdealState, Position, Tree } from '@blueprintjs/core';
import { IconNames } from "@blueprintjs/icons";
import { Icon as SlykitIcon } from 'components/icon';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LAYOUT_OPTIONS } from '/views/explorer/layoutOptions';
import { ObjectDropdown } from 'components/dropdowns/objectDropdown';
import { SavedViewTypes } from 'hooks/savedViews/types';
import { useSavedStateSelectors } from 'hooks/savedViews/hooks';
import { WIDGETS } from 'widgets';

import { ManageViewsActionCreator, ManageViewsStateContext } from 'views/savedViews/useManageViewsState';

const layouts = Object.values(LAYOUT_OPTIONS);

// returns the views that should be displayed in the tree component
const getViews = (dashboards, explorers, widgets, activeItemId) => [
	...dashboards.map((dashboard) => {
		const { metaData: { name, id, isFavorite } } = dashboard;
		return {
			icon: IconNames.DASHBOARD,
			id,
			isFavorite,
			isSelected: id === activeItemId,
			label: name ?? id,
			name: name,
			secondaryLabel: isFavorite && <Icon icon={IconNames.STAR} />,
			type: SavedViewTypes.Dashboard,
		}
	}),
	...explorers.map((explorer) => {
		const { metaData: { name, id, isFavorite } } = explorer;
		return {
			icon: IconNames.GEOSEARCH,
			id,
			isFavorite,
			isSelected: id === activeItemId,
			label: name ?? id,
			name: name,
			secondaryLabel: isFavorite && <Icon icon={IconNames.STAR} />,
			type: SavedViewTypes.Explorer,
		}
	}),
	...widgets.map((widget) => {
		const { type, metaData: { name, id, isFavorite }} = widget;
		return {
			icon: <div css={css`margin-right: 7px;`} className="custom-icon"><SlykitIcon icon={WIDGETS[type].icon} className={"bp3-icon"}/></div>,
			id,
			isFavorite,
			isSelected: id === activeItemId,
			label: (
				<div
					key={id}
					cy-test='views-widget-label'
					draggable={'true'}
					onDragStart={(e) =>
						e.dataTransfer.setData('view', JSON.stringify(widget))
					}
				>
					{name ?? id}
				</div>
			),
			name: name,
			secondaryLabel: isFavorite && <Icon icon={IconNames.STAR} />,
			type: SavedViewTypes.Widget,
		};
	}),
];

export const ManageViewsPanel = ({ openPanel }) => {
	const {
		explorers,
		widgets,
		dashboards,
	} = useSavedStateSelectors();
	const {
		state: {
			isEditing,
			selectedView
		},
		dispatch: manageViewsDispatch
	} = useContext(ManageViewsStateContext);

	const views = useMemo(() => getViews(dashboards, explorers, widgets, selectedView?.metaData?.id), [dashboards, explorers, widgets, selectedView]);
	const [viewFilter, setViewFilter] = useState(VIEW_FILTERS[0]);
	const [viewSort, setViewSort] = useState(SORT_VIEWS_BY[0]);
	const [sortedViews, setSortedViews] = useState(views);
	const [searchFilter, setSearchFilter] = useState('');
	const styles = panelStyles;
	
	// organize views based on filters and sort options
	useEffect(() => {
		let sortViews = [...views];
		// filter with search bar
		sortViews = sortViews.filter(x => x.name.toLowerCase().includes(searchFilter.toLowerCase()));
		// filter by view type
		if (viewFilter.name !== 'all') {
			sortViews = sortViews.filter(x => x.type === viewFilter.name);
		}
		// sort options
		if (viewSort === 'Alphabetical') {
			sortViews.sort((a, b) => {
				if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
				else if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
				return 0;
			});
		}
		else if (viewSort === 'Favorite') {
			sortViews.sort((a, b) => {
				if (a.isFavorite && !b.isFavorite) return -1;
				if (!a.isFavorite && b.isFavorite) return 1;
				return 0;
			});
		}
		setSortedViews(sortViews);
	}, [views, viewFilter, viewSort, searchFilter]);
	
	const openNewPanel = useCallback((viewType, component) => {
		openPanel({
			component: component,
			props: { viewType },
			title: viewType,
		});
	}, [openPanel]);
	
	const panelToRender = useCallback((viewType) => {
		switch (viewType) {
			case SavedViewTypes.Dashboard:
				return EditDashboardPanel;
			default:
				return null;
		}
	}, []);

	useEffect(() => {
		if (isEditing) {
			const viewType = selectedView.metaData.type;
			// manageViewsDispatch(ManageViewsActionCreator.setLayout(layouts[0]));
			// manageViewsDispatch(ManageViewsActionCreator.setRenderComponent(viewType));
			const component = panelToRender(viewType);
			component && openNewPanel(viewType, component);
		}
	}, [isEditing, selectedView, manageViewsDispatch, openNewPanel, panelToRender]);

	const handleNodeSelected = (selectedView) => {
		if (isEditing) return;
		manageViewsDispatch(ManageViewsActionCreator.setRenderComponent(selectedView.type));
		setActiveItemId(selectedView);
		setSortedViews(views => {
			const updatedViews = [...views];
			updatedViews.forEach(view => {
				if (view.id === selectedView.id) {
					view.isSelected = true;
				}
				else { view.isSelected = false; }
			})
			return updatedViews;
		});
	}

	// this handles which side panel component will get rendered
	const handleCreateView = (viewType) => {
		manageViewsDispatch(ManageViewsActionCreator.startCreating());
		manageViewsDispatch(ManageViewsActionCreator.setLayout(layouts[0]));
		manageViewsDispatch(ManageViewsActionCreator.setRenderComponent(viewType));
		// deselect any views previously selected
		manageViewsDispatch(ManageViewsActionCreator.setSelectedView(null))
		const component = panelToRender(viewType);
		openNewPanel(viewType, component);
	}


	const setActiveItemId = useCallback((item) => {
		const view = [
			...dashboards,
			...explorers,
			...widgets
		].filter(x => x.metaData.id === item.id)[0] || null;
		manageViewsDispatch(ManageViewsActionCreator.setSelectedView(view));
	}, [manageViewsDispatch, dashboards, explorers, widgets]);

	// TODO: Add onClick events for Story and Report.
	// TODO: Add Story and Report to SavedViewTypes
	const CreateButtonDropdownContent = ({ onClick }) => {
		return (
			<Menu disabled={isEditing}>
				<MenuItem text="Dashboard" onClick={() => onClick(SavedViewTypes.Dashboard)}/>
				{/* Commented out because they don't have designs yet */}
				{/* <MenuItem text="Story" />
				<MenuItem text="Report" /> */}
			</Menu>
		)
	}

	return (
		<div css={styles.container}>
			<div css={styles.header}>
				<Icon icon={IconNames.SETTINGS} css={styles.settingsIcon}/>
				<div css={styles.headerTextContainer}>
					<H6 css={styles.headerText}>
						Manage Views
					</H6>
					<div css={styles.text}>
						Edit, delete, and search your saved views.
					</div>
					<div css={styles.text}>Create new dashboards and stories.</div>
				</div>
			</div>

			<div css={styles.buttonRow}>
				<Popover 
					content={<CreateButtonDropdownContent onClick={handleCreateView}/>} 
					position={Position.BOTTOM_LEFT} 
					boundary={'viewport'}
					disabled={isEditing}>
					<Button icon={IconNames.ADD} minimal disabled={isEditing} css={styles.button}>
						Create
					</Button>
				</Popover>
			</div>
			<InputGroup 
				css={styles.search} 
				leftIcon={IconNames.SEARCH} 
				placeholder={"Search"} 
				onChange={(e) => setSearchFilter(e.target.value)}
			/>
			<div>
				{/* Filter By View */}
				<ObjectDropdown 
					activeItem={viewFilter}
					options={VIEW_FILTERS}
					optionToString={(filter) => filter.label}
					onSelectionChanged={setViewFilter}/>
				{/* Filter By Time */}
				<ObjectDropdown 
					activeItem={viewSort}
					options={SORT_VIEWS_BY}
					optionToString={(filter) => filter}
					onSelectionChanged={setViewSort}/>
			</div>
			<Tree contents={sortedViews} onNodeClick={handleNodeSelected} css={styles.tree}/>
		</div>
	)
}

const EditDashboardPanel = ({ closePanel }) => {
	const {
		widgets,
	} = useSavedStateSelectors();
	const { 
		state: {
			isCreating,
			isEditing,
			selectedView,
			layout
		},
		dispatch: manageViewsDispatch
	} = useContext(ManageViewsStateContext);

	const views = useMemo(() => getViews([], [], widgets, selectedView?.metaData?.id), [widgets, selectedView]);
	const [viewSort, setViewSort] = useState(SORT_VIEWS_BY[0]);
	const [sortedViews, setSortedViews] = useState(views);
	const [searchFilter, setSearchFilter] = useState('');
	const styles = panelStyles;

	// organize views based on filters and sort options
	useEffect(() => {
		let sortViews = [...views];
		// filter with search bar
		sortViews = sortViews.filter(x => x.name.toLowerCase().includes(searchFilter.toLowerCase()));
		// sort options
		if (viewSort === 'Alphabetical') {
			sortViews.sort((a, b) => {
				if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
				else if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
				return 0;
			});
		}
		else if (viewSort === 'Favorite') {
			sortViews.sort((a, b) => {
				if (a.isFavorite && !b.isFavorite) return -1;
				if (!a.isFavorite && b.isFavorite) return 1;
				return 0;
			});
		}
		setSortedViews(sortViews);
	}, [views, viewSort, searchFilter]);

	useEffect(() => {
		!isCreating && !isEditing && closePanel();
	}, [isCreating, isEditing, closePanel]);

	// return to manage views page
	const handleBack = useCallback(() => {
		manageViewsDispatch(ManageViewsActionCreator.stopCreating());
		manageViewsDispatch(ManageViewsActionCreator.stopEditing());
		if (selectedView !== null) {
			manageViewsDispatch(ManageViewsActionCreator.setRenderComponent(selectedView.metaData.type));
		} 
		closePanel();
	}, [manageViewsDispatch, closePanel, selectedView]);

	const handleLayoutChange = useCallback((changedLayout) => {
		// TODO: Delete views that aren't visible with the selected layout
		manageViewsDispatch(ManageViewsActionCreator.setLayout(changedLayout));
	}, [manageViewsDispatch]);

	return (
		<div css={styles.container}>
		<div css={styles.header}>
			<Button icon={IconNames.CHEVRON_LEFT} minimal onClick={handleBack} css={styles.headerButton}/>
			<div>
				<H6 css={styles.headerText}>
					{isCreating 
						? 'Create Dashboard'
						: isEditing
							? 'Edit Dashboard'
							: null
					}
				</H6>
				<div css={styles.text}>
				{isCreating 
					? 'Drag and drop saved widgets into containers to create a customized dashboard.'
					: isEditing
						? 'Drag and drop saved widgets into containers to create a customized dashboard.'
						: null
				}
				</div>
			</div>
		</div>
		<ObjectDropdown
			activeItem={layout}
			options={layouts}
			optionToString={(layout) => layout?.label}
			onSelectionChanged={handleLayoutChange}
		/>
		<H6 css={styles.savedWidgetText}>Saved Widgets</H6>
		<InputGroup 
			css={styles.search} 
			leftIcon={IconNames.SEARCH} 
			placeholder={"Search"} 
			onChange={(e) => setSearchFilter(e.target.value)}
		/>
		<div>
			{/* Filter By Time */}
			<ObjectDropdown 
				activeItem={viewSort}
				options={SORT_VIEWS_BY}
				optionToString={(filter) => filter}
				onSelectionChanged={setViewSort}/>
		</div>
		<Tree contents={sortedViews} css={styles.tree}/>
		{!views.length > 0 && (
			<div style={{
				height: views.length > 0 ? 'auto' : '100%',
				marginTop: views.length > 0 ? '50px' : 0
				}}>
				<NonIdealState
					icon={'clean'}
					title={'No Widgets'}
					description={'Create a widget on the explorer page.'}
				/>
			</div>
		)}
		</div>
	)
}

const VIEW_FILTERS = [
	{
		name: 'all',
		label: 'All'
	},
	{
		name: 'dashboard',
		label: 'Dashboard'
	},
	{
		name: 'explorer',
		label: 'Explorer'
	},
	// Commented out until you can create a story or a report
	// {
	// 	name: 'story',
	// 	label: 'Story'
	// },
	// {
	// 	name: 'report',
	// 	label: 'Report'
	// },
	{
		name: 'widget',
		label: 'Widget'
	},
];

const SORT_VIEWS_BY = [
	'Most Recent',
	'Alphabetical',
	'Favorite'
];

const panelStyles = {
	container: css`
		border-radius: 3px;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	`,
	header: css`
		padding: 20px 0 10px;
		background-color: rgb(235,241,245);
		display: flex;
	`,
	settingsIcon: css`
		color: #5c7080;
		margin-left: 20px;
		margin-right: 10px;
	`,
	headerButton: css`
		height: min-content;
		margin-top: -5px;
	`,
	headerText: css`
		margin: 0 0 5px;
	`,
	text: css`
		font-size: 12px;
	`,
	buttonRow: css`
		display: flex;
		width: 100%;
		justify-content: flex-end;
	`,
	button: css`
		color: #006699 !important;
		.bp3-icon:first-of-type {
			color: #006699 !important;
		}
	`,
	search: css`
		margin-left: 10px;
		margin-right: 10px;
	`,
	savedWidgetText: css`
		margin-left: 10px;
		padding-top: 10px;
	`,
	tree: css`
		overflow: auto;
		.bp3-tree-node-selected > .bp3-tree-node-content {
			background-color: rgb(235,241,245);
			color: rgb(19,124,189);
			.bp3-tree-node-icon {
				color: rgb(19,124,189);
			}
			.bp3-tree-node-secondary-label > .bp3-icon {
				color: rgb(92,112,128);
			}
			.custom-icon svg{
				color: rgb(19,124,189);
			}
		}
	`,
}